/**
 * The desktop player's filling column (handoff §19.8): a centred 760 px column that is
 * exactly as tall as the room under the screen-header bar, so the text card fills the
 * height and scrolls itself while the answer field stays where it is.
 *
 * The height is the window's bottom minus the column's own top, measured from the real
 * layout (the bar, a language banner), not assumed. It is re-measured whenever the app's
 * scrolling surface changes size, through a `ResizeObserver` (the callback sets state,
 * which a subscription to an outside system may). Until the first measurement, or where
 * there is no layout (jsdom), a fixed allowance for the bar and the shell's padding applies.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { getAppScrollElement } from '../../../utils/appScroll'

/** The shell's bar (64) and its padding above and below (40 + 40). */
const FALLBACK_HEIGHT = 'calc(100dvh - 144px)'
/** What the shell leaves below the column: its own bottom padding. */
const BOTTOM_GAP = 40

export default function FillColumn({ children, testId, onMouseDown }: {
  children: ReactNode
  testId: string
  onMouseDown?: React.MouseEventHandler<HTMLDivElement>
}) {
  const ref = useRef<HTMLDivElement | null>(null)
  const [height, setHeight] = useState<number | null>(null)

  useEffect(() => {
    const scroller = getAppScrollElement()
    if (!scroller || typeof ResizeObserver === 'undefined') return
    const measure = () => {
      const el = ref.current
      if (!el) return
      // The column's top in the scroller's own coordinates, so a scrolled page does not skew it.
      const top = el.getBoundingClientRect().top + scroller.scrollTop
      setHeight(Math.max(360, Math.floor(window.innerHeight - top - BOTTOM_GAP)))
    }
    const observer = new ResizeObserver(measure)
    observer.observe(scroller)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      data-testid={testId}
      onMouseDown={onMouseDown}
      style={{
        width: '100%', maxWidth: 760, margin: '0 auto', boxSizing: 'border-box', display: 'flex', flexDirection: 'column',
        gap: 12, minHeight: 0, height: height ?? FALLBACK_HEIGHT,
      }}
    >
      {children}
    </div>
  )
}
